/**
 * VoiceQuickPicker Component
 *
 * Compact TTS provider and voice selector for the script edit panel.
 * Reuses existing constants (TTS_PROVIDERS, TTS_VOICES) and VoicePreviewButton.
 */

import { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { VoicePreviewButton, type VoicePreviewStatus } from '@/components/VoicePreviewButton';
import { TTS_PROVIDERS, getVoicesForLanguageAndProvider } from '@/constants/generated';
import type { Language, TTSProvider } from '@/constants/generated';

interface VoiceQuickPickerProps {
  ttsProvider: string;
  voiceId: string;
  language: string;
  onProviderChange: (provider: string) => void;
  onVoiceChange: (voiceId: string) => void;
  disabled?: boolean;
}

export const VoiceQuickPicker = memo(function VoiceQuickPicker({
  ttsProvider,
  voiceId,
  language,
  onProviderChange,
  onVoiceChange,
  disabled = false,
}: VoiceQuickPickerProps) {
  const { t } = useTranslation();

  const enabledProviders = useMemo(() => TTS_PROVIDERS.filter((p) => !p.disabled), []);

  const voices = useMemo(
    () => getVoicesForLanguageAndProvider(language as Language, ttsProvider as TTSProvider),
    [language, ttsProvider]
  );

  // Voice the select should display. Falls back to the first available voice when the
  // current voiceId belongs to a different provider (e.g. user just switched providers
  // and the parent state still holds the old voice).
  // Crucially: when no voices are available for the combo, return '' — never the stale
  // voiceId — so the preview button doesn't keep playing the previous provider's voice.
  const effectiveVoiceId = useMemo(() => {
    if (voices.some((v) => v.id === voiceId)) return voiceId;
    return voices[0]?.id || '';
  }, [voices, voiceId]);

  // Sync the parent state when our effective voice diverges from voiceId. Without this,
  // pyttsx3 (and any other provider) would keep its old voice in the parent's state and
  // leak across provider switches when the user clicks the preview/play button.
  useEffect(() => {
    if (effectiveVoiceId && effectiveVoiceId !== voiceId) {
      onVoiceChange(effectiveVoiceId);
    } else if (!effectiveVoiceId && voiceId) {
      onVoiceChange('');
    }
  }, [effectiveVoiceId, voiceId, onVoiceChange]);

  const [previewStatus, setPreviewStatus] = useState<VoicePreviewStatus>('idle');

  const handleProviderChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProvider = e.target.value;
    const newVoices = getVoicesForLanguageAndProvider(
      language as Language,
      newProvider as TTSProvider
    );
    // Reset voice BEFORE flipping the provider so the parent never sees an inconsistent
    // (newProvider, oldVoice) pair — the cause of "robot voice persists after switching".
    onVoiceChange(newVoices.length > 0 ? newVoices[0].id : '');
    onProviderChange(newProvider);
  };

  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-secondary-400">
        {t('scriptPreview.voiceNarrator', { defaultValue: 'Voice' })}
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Provider select */}
        <select
          value={ttsProvider}
          onChange={handleProviderChange}
          disabled={disabled}
          className="flex-1 bg-secondary-700/60 border border-secondary-600/80 rounded-lg px-3 py-2 text-xs text-secondary-200 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 min-h-[36px] transition-colors"
        >
          {enabledProviders.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>

        {/* Voice select + preview (integrated) */}
        <div className="flex flex-1 rounded-lg border border-secondary-600/80 bg-secondary-700/60 overflow-hidden focus-within:ring-1 focus-within:ring-primary-500 focus-within:border-primary-500 transition-colors">
          <select
            value={effectiveVoiceId}
            onChange={(e) => onVoiceChange(e.target.value)}
            disabled={disabled || voices.length === 0}
            className="flex-1 bg-transparent border-none px-3 py-2 text-xs text-secondary-200 focus:outline-none min-h-[36px] min-w-0"
          >
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
            {voices.length === 0 && (
              <option value="" disabled>
                --
              </option>
            )}
          </select>
          <VoicePreviewButton
            provider={ttsProvider}
            voiceId={effectiveVoiceId}
            language={language}
            disabled={disabled || !effectiveVoiceId || voices.length === 0}
            size="md"
            className="flex-shrink-0 min-h-[36px] min-w-[36px] flex items-center justify-center border-l border-secondary-600/80 hover:bg-secondary-600/50 transition-colors"
            onStatusChange={setPreviewStatus}
          />
        </div>
      </div>
      {previewStatus === 'unavailable' && (
        <p className="text-xs text-secondary-400 mt-1">
          Voice sample not available for this voice. Generation will still work normally.
        </p>
      )}
    </div>
  );
});
